import { makeAutoObservable } from "mobx";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
  duration?: number;
  /** One button next to the text (e.g. «Вернуть»); the toast closes after the click. */
  action?: ToastAction;
}

const CHARS_PER_SECOND = 14;
const MIN_MS = { success: 3000, info: 3000, warning: 4500, error: 4500 } as const;
const MAX_MS = { success: 8000, info: 8000, warning: 16000, error: 16000 } as const;
const NOTICE_MS = { success: 1200, info: 1200, warning: 2500, error: 2500 } as const;

export function resolveToastDuration(message: string, type: ToastType, explicit?: number): number {
  if (explicit != null) return explicit;
  const length = message.trim().length;
  const readingMs = Math.ceil(length / CHARS_PER_SECOND) * 1000 + NOTICE_MS[type];
  return Math.min(MAX_MS[type], Math.max(MIN_MS[type], readingMs));
}

type ToastTimer = {
  handle: ReturnType<typeof setTimeout>;
  remaining: number;
  startedAt: number;
  paused: boolean;
};

class ToastStore {
  toasts: ToastMessage[] = [];
  nextId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  show(message: string, type: ToastType = "info", duration?: number) {
    const key = `${type}::${message.trim()}`
    const now = Date.now()
    const prev = this.recentMessageKeys.get(key)
    if (prev != null && now - prev < 5000) {
      return
    }
    this.recentMessageKeys.set(key, now)
    if (this.recentMessageKeys.size > 64) {
      for (const [k, ts] of this.recentMessageKeys) {
        if (now - ts > 5000) this.recentMessageKeys.delete(k)
      }
    }

    const resolved = resolveToastDuration(message, type, duration);
    const id = this.nextId++;
    this.toasts.push({ id, message, type, duration: resolved });
    this.arm(id, resolved);
  }

  /** A toast with an action button. Not deduplicated: every call is its own event (e.g. two hidden phrases). */
  showWithAction(message: string, action: ToastAction, type: ToastType = "info", duration?: number): number {
    const resolved = resolveToastDuration(message, type, duration);
    const id = this.nextId++;
    this.toasts.push({ id, message, type, duration: resolved, action });
    this.arm(id, resolved);
    return id;
  }

  /** Time left before the toast closes by itself (ms); frozen while hovered. null → unknown toast. */
  remainingMs(id: number): number | null {
    const timer = this.timers.get(id);
    if (!timer) return null;
    if (timer.paused) return timer.remaining;
    return Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
  }

  pause(id: number) {
    const timer = this.timers.get(id);
    if (!timer || timer.paused) return;
    clearTimeout(timer.handle);
    timer.remaining = Math.max(400, timer.remaining - (Date.now() - timer.startedAt));
    timer.paused = true;
  }

  resume(id: number) {
    const timer = this.timers.get(id);
    if (!timer || !timer.paused) return;
    timer.paused = false;
    this.arm(id, timer.remaining);
  }

  private recentMessageKeys = new Map<string, number>();
  private timers = new Map<number, ToastTimer>();

  private arm(id: number, ms: number) {
    const handle = setTimeout(() => this.remove(id), ms);
    this.timers.set(id, { handle, remaining: ms, startedAt: Date.now(), paused: false });
  }

  remove(id: number) {
    const timer = this.timers.get(id);
    if (timer) clearTimeout(timer.handle);
    this.timers.delete(id);
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }
}

export const toastStore = new ToastStore();
